import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import { useIntl } from 'react-intl';
import {
  Button, ButtonGroup, ButtonToolbar, DropdownItem, DropdownMenu, DropdownToggle,
  UncontrolledButtonDropdown,
} from 'reactstrap';

import {
  LIST_FILTER_ALL,
} from 'src/util/constants';

import buildTypesOptions, { buildTradeRuleStatesOptions } from './helper';

/**
 *
 * @param {any} root0
 * @param {any} root0.date
 * @param {any} root0.dateUpdateFunc
 * @param {any} root0.filter
 * @param {any} root0.filterFunc
 * @returns {any} - AdminTradeRulesControls component
 */
function AdminTradeRulesControls({
  date = null, dateUpdateFunc, filter, filterFunc,
}) {
  const [selectedDate, setSelectedDate] = useState(date);
  const [myRef, setMyRef] = useState(false);

  const intl = useIntl();

  const closeCalendar = () => {
    myRef.setOpen(false);
  };

  const dateButtonLabel = (d) => (d === null ? '' : d.setLocale(intl.locale).toFormat('DD'));

  const setDate = () => {
    dateUpdateFunc(selectedDate);
  };

  const updateDate = (d) => {
    const newDate = d && DateTime.fromJSDate(d);
    if (newDate && date && newDate.toSeconds() === date.toSeconds()) {
      return;
    }
    setSelectedDate(newDate);
  };

  const applyButtonLabel = intl.formatMessage({
    id: 'admin.admin_trade_rule_controls.date_picker.apply_button.label',
    defaultMessage: 'Apply',
  });

  const renderDatePicker = (onChangeFunc, onCloseFunc) => (
    <DatePicker
      locale={intl.locale}
      ref={(r) => { setMyRef(r); }}
      value={dateButtonLabel(date)}
      selected={selectedDate ? selectedDate.toJSDate() : null}
      onChange={onChangeFunc}
      onCalendarClose={onCloseFunc}
      customInput={<DateButton />}
      shouldCloseOnSelect={false}
      popperPlacement="bottom-end"
      popperModifiers={{
        preventOverflow: {
          enabled: true,
          escapeWithReference: false,
          boundariesElement: 'viewport',
        },
      }}
    >
      <div
        style={{
          clear: 'both',
          textAlign: 'right',
          borderTop: '1px solid #ccc',
          padding: '1em',
        }}
      >
        <button className="btn btn-primary" type="button" onClick={() => (closeCalendar())}>{applyButtonLabel}</button>
      </div>
    </DatePicker>
  );

  const states = buildTradeRuleStatesOptions(intl);

  const typesOptions = buildTypesOptions(intl);

  const filterAllLabel = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_trade_rules.admin_meter_trade_rules_controls.filter.all.label', defaultMessage: 'All' });

  const filterStateLabel = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_trade_rules.admin_meter_trade_rules_controls.filter.state.label', defaultMessage: 'State' });

  const filterTypeLabel = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_trade_rules.admin_meter_trade_rules_controls.filter.type.label', defaultMessage: 'Type' });

  return (
    <div className="trade-history-filters">
      <ButtonToolbar>
        <ButtonGroup className="me-2 mb-2">
          <Button size="sm" className="btn btn-darken" active={!filter.state && !filter.type && !date} onClick={() => filterFunc(null, LIST_FILTER_ALL)}>
            {filterAllLabel}
          </Button>
        </ButtonGroup>

        <ButtonGroup className="me-2 mb-2">
          <UncontrolledButtonDropdown size="sm">
            <DropdownToggle caret className="btn btn-darken" active={!!filter.state}>
              {filter.state ? states[filter.state] : filterStateLabel}
            </DropdownToggle>
            <DropdownMenu>
              {Object.entries(states).map(([value, label]) => (
                <DropdownItem
                  active={filter.state === value}
                  onClick={() => filterFunc('state', value)}
                  key={value}
                >
                  {label}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledButtonDropdown>

          <UncontrolledButtonDropdown size="sm">
            <DropdownToggle caret className="btn btn-darken" active={!!filter.type}>
              {filter.type ? typesOptions[filter.type] : filterTypeLabel }
            </DropdownToggle>
            <DropdownMenu>
              {Object.entries(typesOptions).map(([value, label]) => (
                <DropdownItem
                  active={filter.type === value}
                  onClick={() => filterFunc('type', value)}
                  key={value}
                >
                  {label}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledButtonDropdown>
        </ButtonGroup>

        <ButtonGroup className="ms-2">
          {renderDatePicker(updateDate, setDate)}
        </ButtonGroup>
      </ButtonToolbar>
    </div>
  );
}

AdminTradeRulesControls.propTypes = {
  date: PropTypes.instanceOf(DateTime),
  dateUpdateFunc: PropTypes.func.isRequired,
  filter: PropTypes.shape({
    state: PropTypes.string,
    type: PropTypes.string,
  }).isRequired,
  filterFunc: PropTypes.func.isRequired,
};

// Ref: <https://github.com/Hacker0x01/react-datepicker/issues/862>
//
// eslint-disable-next-line react/prefer-stateless-function
class DateButton extends React.Component {
  render() {
    const {
      onClick,
      disabled,
      value,
    } = this.props;

    return (
      <UncontrolledButtonDropdown size="sm" onClick={() => (onClick())} disabled={disabled}>
        <DropdownToggle caret className="btn btn-darken" active={value !== ''}>
          {value === '' ? (
            <FontAwesomeIcon icon={faCalendar} />
          ) : value}
        </DropdownToggle>
      </UncontrolledButtonDropdown>
    );
  }
}

DateButton.propTypes = {
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  value: PropTypes.string,
};

DateButton.defaultProps = {
  disabled: false,
  onClick: null,
  value: 'loading...',
};

export default AdminTradeRulesControls;

export { DateButton };
